// src/components/LeafletMap.jsx
import React, { useEffect, useRef, useState } from "react";
import L from "leaflet";

/**
 * A fully self-contained Leaflet map component.
 *
 * Props:
 * - lat (number): Center latitude (default: 51.505)
 * - lng (number): Center longitude (default: -0.09)
 * - zoom (number): Initial zoom level (default: 13)
 * - markers (Array): Array of marker objects, each with:
 *    { lat, lng, popupText, markerId }.
 * - onMarkerClick (function): Callback(markerId) => void (optional)
 * - onUserInteraction (function): Called once the user pans/zooms (optional)
 * - mapRef (object): A React ref to store the Leaflet map instance (optional)
 * - spotRefs (object): a ref object that has .current[markerId] for DOM elements
 * - skipAutoFit (boolean): If true, do NOT call fitBounds automatically (default: false)
 *
 * Combined logic:
 * - If markers exist and skipAutoFit === false, fit the map bounds to show all markers.
 * - Otherwise, use lat/lng/zoom.
 * - onMarkerClick is called if provided, and we also highlight the matching text in spotRefs.
 * - Once the user moves/pans/zooms the map, onUserInteraction() is called if provided.
 */

export default function LeafletMap({
  lat = 51.505,
  lng = -0.09,
  zoom = 13,
  markers = [],
  onMarkerClick,         // optional callback when a marker is clicked
  onUserInteraction,     // optional callback on map movestart
  mapRef,
  spotRefs,
  skipAutoFit = false,
}) {
  const mapContainerRef = useRef(null);
  const mapInstanceRef = useRef(null);

  // 1) Track which marker is currently "active" to highlight text
  const [activeHtmlMarkerId, setActiveHtmlMarkerId] = useState(null);
  const oldMarkerIdRef = useRef(null);

  // 2) Initialize the map if it doesn’t exist yet
  useEffect(() => {
    if (!mapInstanceRef.current) {
      mapInstanceRef.current = L.map(mapContainerRef.current).setView(
        [lat, lng],
        zoom
      );

      // Tile layer
      L.tileLayer(
        "https://{s}.basemaps.cartocdn.com/rastertiles/voyager/{z}/{x}/{y}{r}.png",
        {
          maxZoom: 19,
          attribution:
            '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>',
        }
      ).addTo(mapInstanceRef.current);

      // Default marker icon
      const DefaultIcon = L.icon({
        iconUrl: "https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon.png",
        iconRetinaUrl:
          "https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon-2x.png",
        shadowUrl:
          "https://unpkg.com/leaflet@1.9.4/dist/images/marker-shadow.png",
        iconSize: [25, 41],
        iconAnchor: [12, 41],
        popupAnchor: [1, -34],
        shadowSize: [41, 41],
      });
      L.Marker.prototype.options.icon = DefaultIcon;

      // Expose map instance to parent if needed
      if (mapRef) {
        mapRef.current = mapInstanceRef.current;
      }

      // Listen for panning/zooming => call onUserInteraction if provided
      if (onUserInteraction) {
        mapInstanceRef.current.on("movestart", () => {
          onUserInteraction();
        });
      }
    }
  }, [lat, lng, zoom, mapRef, onUserInteraction]);

  // 3) Render markers & handle marker clicks
  useEffect(() => {
    const map = mapInstanceRef.current;
    if (!map) return;

    // Remove any existing markers to avoid duplicates
    map.eachLayer((layer) => {
      if (layer instanceof L.Marker) {
        map.removeLayer(layer);
      }
    });

    // Create new markers
    const createdMarkers = markers.map(({ lat, lng, popupText, markerId }) => {
      const marker = L.marker([lat, lng]).addTo(map);

      if (popupText) {
        marker.bindPopup(popupText);
      }

      marker.on("click", () => {
        // Call external callback if provided
        if (onMarkerClick) {
          onMarkerClick(markerId);
        }

        // Update active marker => triggers highlight
        setActiveHtmlMarkerId(markerId);

        // Scroll text into view
        if (spotRefs?.current?.[markerId]?.scrollIntoView) {
          spotRefs.current[markerId].scrollIntoView({
            behavior: "smooth",
            block: "center",
          });
        }
      });

      return marker;
    });

    // Auto-fit bounds if skipAutoFit = false and we have markers
    if (!skipAutoFit && createdMarkers.length > 0) {
      const group = L.featureGroup(createdMarkers);
      map.fitBounds(group.getBounds(), { padding: [50, 50] });
    } else if (!skipAutoFit && createdMarkers.length === 0) {
      // No markers => just center on lat/lng
      map.setView([lat, lng], zoom);
    }
  }, [markers, spotRefs, skipAutoFit, lat, lng, zoom, onMarkerClick]);

  // 4) Highlight DOM text for the newly active marker, reset old marker’s text color
  useEffect(() => {
    // Reset color of old active marker text
    if (oldMarkerIdRef.current && spotRefs?.current?.[oldMarkerIdRef.current]) {
      spotRefs.current[oldMarkerIdRef.current].style.color = "";
    }
    // Highlight new
    if (activeHtmlMarkerId && spotRefs?.current?.[activeHtmlMarkerId]) {
      spotRefs.current[activeHtmlMarkerId].style.color = "#ff6b6b";
    }
    // Remember the newly selected marker
    oldMarkerIdRef.current = activeHtmlMarkerId;
  }, [activeHtmlMarkerId, spotRefs]);

  // 5) Return the actual map container
  return (
    <div
      ref={mapContainerRef}
      style={{
        width: "100%",
        height: "100%",
        borderRadius: "14px",
        overflow: "hidden",
      }}
      aria-label="Leaflet map container"
    />
  );
}